import api from './api';

export interface NdSignColleague {
  id: number;
  name: string;
  email: string;
}

export interface NdSignSigner {
  id: number;
  email: string;
  name: string;
  order: number;
  status: string;
  embed_url?: string | null;
}

export interface NdSignEnvelope {
  id: number;
  ndsign_id: string;
  title: string;
  status: string;
  doc_filename: string | null;
  created_by: number;
  created_at: string | null;
  updated_at: string | null;
  signers: NdSignSigner[];
  my_embed_url: string | null;
}

export interface CreateEnvelopeSigner {
  name: string;
  email: string;
  order: number;
}

export interface CreateEnvelopeField {
  signer_index: number;
  type: 'signature' | 'initials' | 'date' | 'name';
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
}

const ndsignService = {
  async getColleagues(): Promise<NdSignColleague[]> {
    const response = await api.get('/ndsign/colleagues');
    return (response.data.colleagues || []) as NdSignColleague[];
  },

  async listEnvelopes(): Promise<NdSignEnvelope[]> {
    const response = await api.get('/ndsign/envelopes');
    return (response.data.envelopes || []) as NdSignEnvelope[];
  },

  async getEnvelope(id: number): Promise<NdSignEnvelope> {
    const response = await api.get(`/ndsign/envelopes/${id}`);
    return response.data.envelope as NdSignEnvelope;
  },

  async createEnvelope(params: {
    pdf: Blob;
    filename: string;
    title: string;
    message?: string;
    expiresDays?: number;
    signers: CreateEnvelopeSigner[];
    fields?: CreateEnvelopeField[];
  }): Promise<NdSignEnvelope> {
    const form = new FormData();
    form.append('pdf', params.pdf, params.filename);
    form.append('title', params.title);
    if (params.message) {
      form.append('message', params.message);
    }
    if (params.expiresDays != null) {
      form.append('expires_days', String(params.expiresDays));
    }
    form.append('signers', JSON.stringify(params.signers));
    form.append('fields', JSON.stringify(params.fields || []));

    const response = await api.post('/ndsign/envelopes', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.envelope as NdSignEnvelope;
  },

  async refreshEnvelope(id: number): Promise<NdSignEnvelope> {
    const response = await api.post(`/ndsign/envelopes/${id}/refresh`);
    return response.data.envelope as NdSignEnvelope;
  },

  async downloadDocument(id: number, fallbackName = 'signed-document.pdf'): Promise<void> {
    const response = await api.get(`/ndsign/envelopes/${id}/document`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fallbackName;
    link.click();
    URL.revokeObjectURL(url);
  },
};

export default ndsignService;
